import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Button,
  Badge,
  Spinner,
  Flex,
  Icon,
  Separator,
} from "@chakra-ui/react";
import {
  BiCheck,
  BiTrash,
  BiRefresh,
  BiChevronDown,
  BiChevronUp,
} from "react-icons/bi";
import { useNotificationContext } from "../../../../Contexts/NotificationContext";
import useThemeColors from "../../../../hooks/useThemeColors";
import NotificationItem from "./NotificationItem";
import { Tooltip } from "../../../ui/tooltip";
import CustomPopover from "../../../CustomUi/CustomPopover";
import { NotificationType } from "../../../../services/Enums";

interface NotificationCenterProps {
  trigger?: React.ReactNode;
  isMobile?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  trigger,
  open,
  setOpen,
}) => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    connectionState,
    markAllAsRead,
    clearAllNotifications,
    fetchNotifications,
    getNotificationTypeLabel,
  } = useNotificationContext();

  const colors = useThemeColors();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState<NotificationType | "all">(
    "all"
  );

  const filteredNotifications =
    selectedType === "all"
      ? notifications
      : notifications.filter((n) => n.type === Number(selectedType));

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleClearAll = async () => {
    await clearAllNotifications();
  };

  const handleRefresh = async () => {
    await fetchNotifications();
  };

  function NotificationCenterHeader() {
    return (
      <Flex justify="space-between" align="center">
        <Text fontWeight="semibold" fontSize="md">
          Notifications
          {unreadCount > 0 && (
            <Badge ml={2} colorPalette="red" fontSize="xs">
              {unreadCount}
            </Badge>
          )}
        </Text>
        <HStack gap={1}>
          <Tooltip
            content={`Connection: ${connectionState}`}
            positioning={{ placement: "top" }}
          >
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg={
                connectionState === "Connected"
                  ? "green.400"
                  : connectionState === "Failed"
                    ? "red.400"
                    : connectionState === "Fallback"
                      ? "orange.400"
                      : "yellow.400"
              }
              mr={3}
            />
          </Tooltip>
          <Tooltip content="Refresh" positioning={{ placement: "top" }} asChild>
            <IconButton
              aria-label="Refresh notifications"
              size="xs"
              variant="ghost"
              onClick={handleRefresh}
              loading={isLoading}
            >
              <BiRefresh />
            </IconButton>
          </Tooltip>
          <Tooltip content="Filters" positioning={{ placement: "top" }} asChild>
            <IconButton
              aria-label="Toggle filters"
              size="xs"
              variant="ghost"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <BiChevronUp /> : <BiChevronDown />}
            </IconButton>
          </Tooltip>
        </HStack>
      </Flex>
    );
  }

  function NotificationCenterBody() {
    return (
      <>
        {showFilters && (
          <Box p={3} borderBottom="1px" borderColor={colors.border.primary}>
            <VStack gap={2} align="stretch">
              <Text fontSize="sm" fontWeight="medium">
                Filter by type:
              </Text>
              <Flex wrap="wrap" gap={2}>
                {[
                  "all",
                  NotificationType.DirectMessage,
                  NotificationType.BroadcastMessage,
                  NotificationType.SystemMessage,
                  NotificationType.DashboardUpdate,
                  NotificationType.ResultUpdate,
                ].map((type) => (
                  <Badge
                    key={type}
                    variant={selectedType === type ? "solid" : "outline"}
                    colorPalette={selectedType === type ? "blue" : "gray"}
                    cursor="pointer"
                    onClick={() =>
                      setSelectedType(type as NotificationType | "all")
                    }
                    px={2}
                    py={1}
                    fontSize="xs"
                  >
                    {type === "all"
                      ? "All"
                      : getNotificationTypeLabel(type as NotificationType)
                          .label}
                  </Badge>
                ))}
              </Flex>
            </VStack>
          </Box>
        )}

        {/* Actions Bar */}
        {(unreadCount > 0 || notifications.length > 0) && (
          <Box py={3} borderBottom="1px" borderColor={colors.border.primary}>
            <Flex justify="space-between" align="center">
              <HStack gap={2}>
                {unreadCount > 0 && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={handleMarkAllAsRead}
                  >
                    <Icon as={BiCheck} mr={2} />
                    Mark all read
                  </Button>
                )}
              </HStack>
              {notifications.length > 0 && (
                <Button
                  size="xs"
                  variant="outline"
                  colorPalette="red"
                  onClick={handleClearAll}
                >
                  <Icon as={BiTrash} mr={2} />
                  Clear all
                </Button>
              )}
            </Flex>
          </Box>
        )}

        {/* Notifications List */}
        <Box>
          {isLoading ? (
            <Flex justify="center" p={6}>
              <Spinner size="md" />
            </Flex>
          ) : filteredNotifications.length === 0 ? (
            <Box p={6} textAlign="center">
              <Text fontSize="sm">
                {selectedType === "all"
                  ? "No notifications yet"
                  : `No ${getNotificationTypeLabel(
                      selectedType as NotificationType
                    ).label.toLowerCase()} notifications`}
              </Text>
            </Box>
          ) : (
            <VStack gap={0} align="stretch">
              {/* Unread Notifications */}
              {filteredNotifications.filter((n) => !n.isRead).length > 0 && (
                <>
                  <Box px={3} py={2} bg={colors.background.secondary}>
                    <Text fontSize="xs" fontWeight="medium">
                      UNREAD
                    </Text>
                  </Box>
                  {filteredNotifications
                    .filter((n) => !n.isRead)
                    .map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                </>
              )}

              {/* Read Notifications */}
              {filteredNotifications.filter((n) => n.isRead).length > 0 && (
                <>
                  {filteredNotifications.filter((n) => !n.isRead).length >
                    0 && <Separator />}
                  <Box px={3} py={2} bg={colors.background.secondary}>
                    <Text fontSize="xs" fontWeight="medium">
                      READ
                    </Text>
                  </Box>
                  {filteredNotifications
                    .filter((n) => n.isRead)
                    .slice(0, 5) // Show only first 5 read notifications
                    .map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                </>
              )}
            </VStack>
          )}
        </Box>

        {/* View All Link */}
        {notifications.length > 0 && (
          <Box p={3} borderTop="1px" borderColor={colors.border.primary}>
            <Button
              size="sm"
              variant="ghost"
              w="full"
              onClick={() => {
                navigate("/admin/notifications");
                setOpen?.(false);
              }}
            >
              View all notifications
            </Button>
          </Box>
        )}
      </>
    );
  }

  return (
    <CustomPopover
      setOpen={setOpen}
      open={open}
      trigger={trigger}
      children={undefined}
      w={{ base: "95vw", md: "400px" }}
      placement="bottom-end"
      maxH={"600px"}
      header={<NotificationCenterHeader />}
      body={<NotificationCenterBody />}
    ></CustomPopover>
  );
};

export default NotificationCenter;
